"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import {
  HardDriveDownload,
  CircleCheckBig,
  ArrowRight,
  BookDown,
  TriangleAlert,
  RotateCcw,
  Cloud,
  Wrench,
  BarChart3,
  Shield,
  Database,
  Smartphone,
  Monitor,
  Server,
  Network,
  Code,
  Phone,
  CalendarDays,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import productService, { Product } from "../../../../services/productService";
import styles from "./page.module.css";
import { buildProductMediaUrl } from "@/utils/productMediaUrl";

const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "https://sdtc.vn");

// Icon mapping giống Vue component
const iconMap: { [key: string]: LucideIcon } = {
  Cloud,
  Wrench,
  BarChart3,
  Shield,
  Database,
  Smartphone,
  Monitor,
  Server,
  Network,
  Code,
};

const getIconComponent = (iconString?: string): LucideIcon => {
  if (!iconString) return Cloud;
  if (typeof iconString === "object" && iconString) {
    return iconString as LucideIcon;
  }
  return iconMap[iconString] || Cloud;
};

// Helper function để xử lý features giống Vue component
const processFeatures = (features: any): string[] => {
  if (!features) {
    return [];
  }

  let featuresArray: string[] = [];

  if (Array.isArray(features)) {
    // Xử lý trường hợp array có 1 phần tử là string JSON
    if (features.length === 1 && typeof features[0] === "string") {
      try {
        const parsed = JSON.parse(features[0]);
        if (Array.isArray(parsed)) {
          featuresArray = parsed.filter(
            (f: any) => f && String(f).trim().length > 0
          );
        } else {
          featuresArray = features.filter(
            (f: any) => f && String(f).trim().length > 0
          );
        }
      } catch {
        // Nếu không parse được, xử lý như string thường
        featuresArray = features.filter(
          (f: any) => f && String(f).trim().length > 0
        );
      }
    } else {
      // Xử lý array bình thường
      featuresArray = features.filter(
        (f: any) => f && String(f).trim().length > 0
      );
    }
  } else if (typeof features === "string") {
    try {
      const parsed = JSON.parse(features);
      if (Array.isArray(parsed)) {
        featuresArray = parsed.filter(
          (f: any) => f && String(f).trim().length > 0
        );
      } else {
        featuresArray = features
          .split(",")
          .map((f: string) => f.trim().replace(/^["']|["']$/g, ""))
          .filter((f: string) => f.length > 0);
      }
    } catch {
      // Nếu không parse được JSON, chia thành array bằng dấu phẩy
      const cleanString = features.replace(/^\[|\]$/g, "").trim();

      featuresArray = cleanString
        .split(",")
        .map((f: string) => f.trim())
        .map((f: string) => f.replace(/^["']|["']$/g, ""))
        .filter((f: string) => f.length > 0);
    }
  } else if (features && typeof features === "object") {
    // Nếu là object, thử convert thành array
    try {
      featuresArray = Object.values(features)
        .map((f: any) => String(f).trim())
        .filter((f: string) => f.length > 0);
    } catch {
      featuresArray = [];
    }
  }

  return featuresArray;
};

// Helper function để tạo slug từ title
const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Loại bỏ dấu
    .replace(/[đĐ]/g, "d")
    .replace(/[^a-z0-9\s-]/g, "") // Loại bỏ ký tự đặc biệt
    .trim()
    .replace(/\s+/g, "-") // Thay khoảng trắng bằng dấu gạch ngang
    .replace(/-+/g, "-") // Loại bỏ nhiều dấu gạch ngang liên tiếp
    .replace(/^-+|-+$/g, ""); // Loại bỏ dấu gạch ngang ở đầu và cuối
};

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:7001/api";

interface ProductDetailClientProps {
  id: string;
}

export default function ProductDetailClient({ id }: ProductDetailClientProps) {
  const router = useRouter();
  const productId = id;

  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeImageSrc, setActiveImageSrc] = useState<string | null>(null);
  const authorName =
    product?.productCreator?.fullName ||
    (product?.createdBy && typeof product.createdBy === "object"
      ? product.createdBy.fullName
      : undefined) ||
    "Đội ngũ SDTC";
  const updatedLabel = product?.updatedAt
    ? new Date(product.updatedAt).toLocaleDateString("vi-VN")
    : "Chưa cập nhật";

  // Processed features giống computed property trong Vue
  const processedFeatures = useMemo(() => {
    if (!product || !product.features) {
      return [];
    }
    return processFeatures(product.features);
  }, [product]);

  useEffect(() => {
    if (productId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadProductData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Update metadata dynamically for SEO (client-side fallback)
  useEffect(() => {
    if (!product) return;

    const title = `${product.title} | Sản phẩm SDTC`;
    const description =
      product.description ||
      `Khám phá ${product.title} - Sản phẩm chất lượng từ SDTC`;
    const imageUrl =
      product.dashboardImage ||
      `${SITE_URL}/assets/sdtc-image/trang-chu/Logo/sdtc.webp`;
    // Sử dụng slug trong URL thay vì ID
    const productSlug = createSlug(product.title);
    const url = `${SITE_URL}/san-pham/${productSlug}`;
    const keywords = [
      product.title,
      product.category,
      "SDTC",
      "sản phẩm",
      "phần mềm",
      ...(product.features && Array.isArray(product.features)
        ? product.features.slice(0, 5)
        : []),
    ]
      .filter(Boolean)
      .join(", ");

    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (
      name: string,
      content: string,
      attribute: string = "name",
    ) => {
      let element = document.querySelector(
        `meta[${attribute}="${name}"]`,
      ) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Basic meta tags
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);

    // Open Graph tags
    updateMetaTag("og:title", title, "property");
    updateMetaTag("og:description", description, "property");
    updateMetaTag("og:url", url, "property");
    updateMetaTag("og:image", imageUrl, "property");
    updateMetaTag("og:type", "website", "property");
    updateMetaTag("og:site_name", "SDTC", "property");
    updateMetaTag("og:locale", "vi_VN", "property");

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", imageUrl);

    // Canonical URL
    let canonical = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Cleanup function
    return () => {
      // Reset to default title on unmount
      document.title = "SDTC - Sea Dragon Technology";
    };
  }, [product]);

  const getImageUrl = (url?: string) => buildProductMediaUrl(url);

  const closeImageModal = useCallback(() => {
    setIsImageModalOpen(false);
    setActiveImageSrc(null);
  }, []);

  const openImageModal = (imageSrc?: string) => {
    if (!imageSrc) return;
    setActiveImageSrc(imageSrc);
    setIsImageModalOpen(true);
  };

  useEffect(() => {
    if (!isImageModalOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeImageModal();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isImageModalOpen, closeImageModal]);

  const loadProductData = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      setError(null);

      // Lấy thông tin sản phẩm chính
      const productResponse = await productService.getProductById(productId);
      const productData = productResponse.data || productResponse;

      // Xử lý features
      const featuresArray = processFeatures(productData.features);

      const processedProduct: Product = {
        ...productData,
        // Xử lý URL hình ảnh và video
        dashboardImage: productData.dashboardImage
          ? getImageUrl(productData.dashboardImage)
          : "/assets/sdtc-image/trang-chu/Logo/sdtc.webp",
        videoFile: productData.videoFile
          ? getImageUrl(productData.videoFile)
          : undefined,
        zipFile: productData.zipFile
          ? getImageUrl(productData.zipFile)
          : undefined,
        guideFile: productData.guideFile
          ? getImageUrl(productData.guideFile)
          : undefined,
        // Xử lý features thành array
        features: featuresArray,
      };

      setProduct(processedProduct);

      // Thay đổi URL từ ID sang slug nếu URL hiện tại là ID
      const currentSlug = createSlug(processedProduct.title);
      const currentPath = window.location.pathname;

      const isCurrentPathId =
        /^\/san-pham\/\d+$/.test(currentPath) ||
        currentPath === `/san-pham/${productId}`;

      if (isCurrentPathId && currentPath !== `/san-pham/${currentSlug}`) {
        router.replace(`/san-pham/${currentSlug}`, { scroll: false });
      }

      // Lấy danh sách sản phẩm liên quan (loại trừ sản phẩm hiện tại)
      const relatedData = await productService.getProducts({
        status: "active",
        limit: 3,
      });

      const productsArray =
        relatedData.data?.products ||
        (Array.isArray(relatedData) ? relatedData : []);

      setRelatedProducts(
        productsArray
          .filter((p: Product) => p.id !== productId)
          .map((p: Product) => ({
            ...p,
            dashboardImage: p.dashboardImage
              ? getImageUrl(p.dashboardImage)
              : "/assets/sdtc-image/trang-chu/Logo/sdtc.webp",
          }))
          .slice(0, 3),
      );
    } catch (err) {
      console.error("Lỗi khi tải dữ liệu sản phẩm:", err);
      setError("Không thể tải thông tin sản phẩm. Vui lòng thử lại sau.");

      // Fallback data nếu API lỗi
      setProduct({
        id: productId,
        title: "Sản phẩm không tìm thấy",
        category: "Khác" as any,
        description: "Không thể tải thông tin sản phẩm",
        status: "inactive",
        views: 0,
        downloads: 0,
        rating: 0,
        comments: 0,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        icon: "Cloud",
        iconColor: "#EF4444",
        dashboardImage: "/assets/sdtc-image/trang-chu/Logo/sdtc.webp",
        overview: "Không thể tải thông tin chi tiết về sản phẩm này.",
        features: ["Thông tin sản phẩm không khả dụng"],
      } as Product);
      setRelatedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadProduct = async () => {
    try {
      if (!product?.zipFile) {
        showToast("File sản phẩm không khả dụng", "error");
        return;
      }

      showToast("Đang tải xuống sản phẩm...", "info");

      const getOriginalFileName = (url: string): string => {
        try {
          const fileName = url.split("/").pop() || "";
          return fileName.split("?")[0];
        } catch {
          return `${product.title.replace(/[^a-z0-9]/gi, "_")}.zip`;
        }
      };

      const originalFileName = getOriginalFileName(product.zipFile);

      try {
        const response = await fetch(product.zipFile, {
          method: "GET",
          headers: {
            Accept: "application/zip, application/octet-stream, */*",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = originalFileName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        showToast("Tải xuống sản phẩm thành công!", "success");
      } catch (fetchError) {
        console.warn("Fetch failed, using direct link:", fetchError);

        const link = document.createElement("a");
        link.href = product.zipFile;
        link.download = originalFileName;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        showToast("Đang tải xuống sản phẩm...", "info");
      }
    } catch (error) {
      console.error("Lỗi khi tải xuống sản phẩm:", error);
      showToast("Không thể tải xuống sản phẩm", "error");
    }
  };

  const downloadGuide = async () => {
    try {
      if (!product?.guideFile) {
        showToast("File hướng dẫn không khả dụng", "error");
        return;
      }

      showToast("Đang tải xuống file hướng dẫn...", "info");

      try {
        const response = await fetch(product.guideFile, {
          method: "GET",
          headers: {
            Accept:
              "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, */*",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const urlParts = product.guideFile.split("/");
        let fileName =
          urlParts[urlParts.length - 1] ||
          `${product.title.replace(/[^a-z0-9]/gi, "_")}_huong_dan.pdf`;

        if (!fileName.includes(".")) {
          const contentType = response.headers.get("content-type");
          if (contentType?.includes("pdf")) {
            fileName = `${product.title.replace(
              /[^a-z0-9]/gi,
              "_",
            )}_huong_dan.pdf`;
          } else if (
            contentType?.includes("word") ||
            contentType?.includes("document")
          ) {
            fileName = `${product.title.replace(
              /[^a-z0-9]/gi,
              "_",
            )}_huong_dan.docx`;
          } else {
            fileName = `${product.title.replace(
              /[^a-z0-9]/gi,
              "_",
            )}_huong_dan`;
          }
        } else {
          fileName = fileName.replace(/[^a-z0-9._-]/gi, "_");
        }

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        showToast("Tải xuống file hướng dẫn thành công!", "success");
      } catch (fetchError) {
        console.warn("Fetch failed, using direct link:", fetchError);

        const urlParts = product.guideFile.split("/");
        let fileName =
          urlParts[urlParts.length - 1] ||
          `${product.title.replace(/[^a-z0-9]/gi, "_")}_huong_dan.pdf`;
        fileName = fileName.replace(/[^a-z0-9._-]/gi, "_");

        const link = document.createElement("a");
        link.href = product.guideFile;
        link.download = fileName;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        showToast("Đang tải xuống file hướng dẫn...", "info");
      }
    } catch (error: any) {
      console.error("Lỗi khi tải xuống file hướng dẫn:", error);
      showToast(
        "Không thể tải xuống file hướng dẫn: " +
          (error?.message || "Unknown error"),
        "error",
      );
    }
  };

  const contactUs = () => {
    router.push("/dat-lich");
  };

  const viewProduct = (nextProductId: string, productTitle?: string) => {
    if (productTitle) {
      const slug = createSlug(productTitle);
      router.push(`/san-pham/${slug}` as any);
    } else {
      router.push(`/san-pham/${nextProductId}` as any);
    }
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const currentSrc = event.currentTarget.src;
    const fallbackSrc = "/assets/sdtc-image/trang-chu/carousel/carousel-1.webp";
    if (!currentSrc.includes("carousel-1.webp")) {
      event.currentTarget.src = fallbackSrc;
    }
  };

  const showToast = (
    message: string,
    type: "success" | "error" | "warning" | "info" = "info",
  ) => {
    try {
      toast[type](message, {
        position: "top-right",
        autoClose: type === "error" ? 4000 : 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch {
      // Toast không khả dụng, bỏ qua
    }
  };

  if (!productId) {
    return null;
  }

  const structuredData = product
    ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: product.title,
        description: product.description,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "VND",
          availability: "https://schema.org/InStock",
        },
        ...(product.rating
          ? {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: product.rating,
                ratingCount: product.comments || 1,
              },
            }
          : {}),
        image:
          product.dashboardImage ||
          `${SITE_URL}/assets/sdtc-image/trang-chu/Logo/sdtc.webp`,
        author: {
          "@type": "Organization",
          name: authorName,
        },
        datePublished: product.createdAt,
        dateModified: product.updatedAt,
        ...(product.features &&
        Array.isArray(product.features) &&
        product.features.length > 0
          ? {
              featureList: product.features.map((feature: string) => ({
                "@type": "SoftwareFeature",
                name: feature,
              })),
            }
          : {}),
      }
    : null;

  return (
    <>
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
      <div className={styles.productDetailPage}>
        <div className={styles.container}>
          {/* Loading State */}
          {loading && (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>
                Đang tải thông tin sản phẩm...
              </p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>
                <TriangleAlert className={styles.customIcon} />
              </div>
              <h2 className={styles.errorTitle}>Lỗi tải dữ liệu</h2>
              <p className={styles.errorMessage}>{error}</p>
              <button onClick={loadProductData} className={styles.btnRetry}>
                <RotateCcw className={styles.customIcon} />
                Thử lại
              </button>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && product && (
            <div className={styles.mainContent}>
              {/* Left Side - Product Content */}
              <div className={styles.productContent}>
                {/* Product Header */}
                <div className={styles.productHeader}>
                  <div className={styles.productHeaderTop}>
                    <div className={styles.productIcon}>
                      {(() => {
                        const IconComponent = getIconComponent(product.icon);
                        return (
                          <IconComponent
                            className={styles.customIcon}
                            style={{ color: product.iconColor || "#265ff3" }}
                          />
                        );
                      })()}
                    </div>
                    <div className={styles.productCategoryTag}>
                      {product.category}
                    </div>
                  </div>
                  <h1 className={styles.productTitle}>{product.title}</h1>
                  <p className={styles.productSubtitle}>
                    {product.description}
                  </p>
                  <div className={styles.productMeta}>
                    <div className={styles.productMetaItem}>
                      <UserRound className={styles.metaIcon} />
                      <span>{authorName}</span>
                    </div>
                    <div className={styles.productMetaItem}>
                      <CalendarDays className={styles.metaIcon} />
                      <span>Cập nhật {updatedLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Dashboard Image Section */}
                <div className={styles.dashboardSection}>
                  <div className={styles.dashboardContainer}>
                    <img
                      src={product.dashboardImage}
                      alt={`${product.title} Dashboard`}
                      className={styles.dashboardImage}
                      onError={handleImageError}
                      onClick={() => openImageModal(product.dashboardImage)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          openImageModal(product.dashboardImage);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Phóng to hình ảnh sản phẩm"
                    />
                  </div>
                </div>

                {/* Product Overview */}
                {product.overview && (
                  <div className={styles.overviewSection}>
                    <h2 className={styles.sectionTitle}>
                      Tổng Quan Sản Phẩm
                    </h2>
                    <div
                      className={styles.overviewText}
                      dangerouslySetInnerHTML={{ __html: product.overview }}
                    />
                  </div>
                )}

                {/* Key Features */}
                {processedFeatures.length > 0 && (
                  <div className={styles.featuresSection}>
                    <h2 className={styles.sectionTitle}>Tính Năng Chính</h2>
                    <ul className={styles.featuresList}>
                      {processedFeatures.map((feature, index) => (
                        <li
                          key={`feature-${index}`}
                          className={styles.featureItem}
                        >
                          <CircleCheckBig className={styles.customIcon} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Video Section */}
                {product.videoFile && (
                  <div className={styles.videoSection}>
                    <h2 className={styles.sectionTitle}>Video Hướng Dẫn</h2>
                    <div className={styles.videoContainer}>
                      <div className={styles.videoWrapper}>
                        <video
                          controls
                          preload="metadata"
                          className={styles.videoPlayer}
                          poster="/assets/sdtc-image/trang-chu/Logo/sdtc.webp"
                        >
                          <source src={product.videoFile} type="video/mp4" />
                          <p>Trình duyệt của bạn không hỗ trợ video HTML5.</p>
                        </video>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ready to Get Started */}
                <div className={styles.getStartedSection}>
                  <h2 className={styles.sectionTitle}>Sẵn Sàng Bắt Đầu?</h2>
                  <p className={styles.getStartedDescription}>
                    Tải xuống sản phẩm và hướng dẫn sử dụng để bắt đầu hành
                    trình của bạn.
                  </p>

                  <div className={styles.downloadButtons}>
                    {product.zipFile ? (
                      <button
                        className={styles.btnDownloadProduct}
                        onClick={downloadProduct}
                      >
                        <HardDriveDownload className={styles.customIcon} />
                        <span>Tải Xuống Sản Phẩm</span>
                      </button>
                    ) : (
                      <div>
                        <button
                          className={styles.btnContact}
                          onClick={contactUs}
                        >
                          <Phone className={styles.customIcon} />
                          <span>Liên Hệ Tư Vấn</span>
                        </button>
                      </div>
                    )}

                    {product.guideFile && (
                      <button
                        className={styles.btnDownloadGuide}
                        onClick={downloadGuide}
                      >
                        <BookDown className={styles.customIcon} />
                        <span>Tải Xuống Hướng Dẫn</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side - Related Products */}
              {relatedProducts.length > 0 && (
                <div className={styles.relatedProducts}>
                  <h3 className={styles.relatedTitle}>Sản Phẩm Liên Quan</h3>
                  <div className={styles.relatedList}>
                    {relatedProducts.map((relatedProduct) => {
                      const RelatedIcon = getIconComponent(relatedProduct.icon);
                      return (
                        <div
                          key={relatedProduct.id}
                          className={styles.relatedCard}
                          onClick={() =>
                            viewProduct(relatedProduct.id, relatedProduct.title)
                          }
                        >
                          <div className={styles.relatedIcon}>
                            <RelatedIcon
                              className={styles.customIcon}
                              style={{
                                color: relatedProduct.iconColor || "#265ff3",
                              }}
                            />
                          </div>
                          <div className={styles.relatedInfo}>
                            <h4 className={styles.relatedName}>
                              {relatedProduct.title}
                            </h4>
                            <p className={styles.relatedCategory}>
                              {relatedProduct.category}
                            </p>
                            <p className={styles.relatedDescription}>
                              {relatedProduct.description}
                            </p>
                          </div>
                          <div className={styles.relatedArrow}>
                            <ArrowRight className={styles.customIcon} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {isImageModalOpen && activeImageSrc && (
        <div
          className={styles.imageModalOverlay}
          onClick={closeImageModal}
          role="dialog"
          aria-modal="true"
          aria-label="Hình ảnh phóng to"
        >
          <div
            className={styles.imageModalContent}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeImageModal}
              className={styles.imageModalClose}
              aria-label="Đóng hình ảnh phóng to"
            >
              ×
            </button>
            <img
              src={activeImageSrc}
              alt={`${product?.title ?? "Sản phẩm"} - hình ảnh phóng to`}
              className={styles.imageModalImage}
              onError={handleImageError}
            />
          </div>
        </div>
      )}
    </>
  );
}


